import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';
import { PushNotifications } from '@capacitor/push-notifications';

// Check if running on native platform
export const isNative = Capacitor.isNativePlatform();
export const platform = Capacitor.getPlatform();

// Initialize native features
export const initializeNativeFeatures = async () => {
  if (!isNative) return;

  try {
    // Configure status bar with Minted teal color
    await StatusBar.setStyle({ style: Style.Light });
    await StatusBar.setBackgroundColor({ color: '#00c9b8' });
    
    // Hide splash screen after app loads
    await SplashScreen.hide();
  } catch (error) {
    console.log('Error initializing native features:', error);
  }
};

// Initialize OneSignal native SDK (Android/iOS push)
export const initNativeOneSignal = async () => {
  if (!isNative) return;
  try {
    const { default: OneSignal } = await import('onesignal-cordova-plugin');
    OneSignal.initialize(process.env.REACT_APP_ONESIGNAL_APP_ID);
    console.log('OneSignal native initialized');
  } catch (error) {
    console.log('OneSignal native init error:', error);
  }
};

// Link the device to the logged-in Minted account for targeted pushes
export const nativeOneSignalLogin = async (userId) => {
  if (!isNative) return;
  try {
    const { default: OneSignal } = await import('onesignal-cordova-plugin');
    OneSignal.login(String(userId));
  } catch (error) {
    console.log('OneSignal native login error:', error);
  }
};

export const nativeOneSignalLogout = async () => {
  if (!isNative) return;
  try {
    const { default: OneSignal } = await import('onesignal-cordova-plugin');
    OneSignal.logout();
  } catch (error) {
    console.log('OneSignal native logout error:', error);
  }
};

// Ask for push permission via the native OneSignal SDK (triggered from the friendly in-app card)
export const nativeRequestPushPermission = async () => {
  if (!isNative) return false;
  try {
    const { default: OneSignal } = await import('onesignal-cordova-plugin');
    const accepted = await OneSignal.Notifications.requestPermission(true);
    return !!accepted;
  } catch (error) {
    console.log('OneSignal native permission error:', error);
    return false;
  }
};

export const nativePushPermissionGranted = async () => {
  if (!isNative) return false;
  try {
    const { default: OneSignal } = await import('onesignal-cordova-plugin');
    return !!OneSignal.Notifications.hasPermission();
  } catch (error) {
    return false;
  }
};

// Request push notification permissions (iOS)
export const requestPushPermissions = async () => {
  if (!isNative) return { granted: false };

  try {
    // Check current permission status
    const permStatus = await PushNotifications.checkPermissions();
    
    if (permStatus.receive === 'prompt') {
      // Request permission
      const result = await PushNotifications.requestPermissions();
      return { granted: result.receive === 'granted' };
    }
    
    return { granted: permStatus.receive === 'granted' };
  } catch (error) {
    console.log('Error requesting push permissions:', error);
    return { granted: false };
  }
};

// Register for push notifications
export const registerPushNotifications = async (onTokenReceived, onNotificationReceived) => {
  if (!isNative) return;

  try {
    // Register with Apple Push Notification service
    await PushNotifications.register();

    // Listen for registration token
    PushNotifications.addListener('registration', (token) => {
      console.log('Push registration token:', token.value);
      if (onTokenReceived) onTokenReceived(token.value);
    });

    // Listen for registration errors
    PushNotifications.addListener('registrationError', (error) => {
      console.error('Push registration error:', error);
    });

    // Listen for push notifications received
    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      console.log('Push notification received:', notification);
      if (onNotificationReceived) onNotificationReceived(notification);
    });

    // Listen for push notification action performed
    PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
      console.log('Push notification action:', notification);
    });
  } catch (error) {
    console.log('Error registering push notifications:', error);
  }
};

export default {
  isNative,
  platform,
  initializeNativeFeatures,
  requestPushPermissions,
  registerPushNotifications
};
